import { useMutation, useQueryClient } from '@tanstack/react-query';

import type { Shift } from '@/openapi';
import { ScheduleConfigService } from '@/openapi';
import { queryKeys } from '@/util/queryKeys';

interface ShiftRerankMutationParams {
  startIndex: number;
  endIndex: number;
  subjectShiftId: number;
  targetShiftId: number;
}

export const useShiftRerankMutation = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (mutationVars: ShiftRerankMutationParams) => {
      const { subjectShiftId, targetShiftId } = mutationVars;
      return ScheduleConfigService.scheduleConfigServiceRerankShifts({
        query: {
          subjectId: subjectShiftId,
          targetId: targetShiftId,
        },
      });
    },
    onMutate: async (mutationVars: ShiftRerankMutationParams) => {
      // Cancel any outgoing refetches, snapshot current values
      await queryClient.cancelQueries({ queryKey: queryKeys.shifts.list() });
      const previousShifts = queryClient.getQueryData<Shift[]>(
        queryKeys.shifts.list()
      );

      // Optimistically update to the new value
      if (previousShifts) {
        const newShifts = [...previousShifts];
        const [removed] = newShifts.splice(mutationVars.startIndex, 1);
        newShifts.splice(mutationVars.endIndex, 0, removed);
        queryClient.setQueryData(queryKeys.shifts.list(), newShifts);
      }

      // Return a context object with the snapshot value
      return { previousShifts };
    },
    onError: (_err, _vars, context) => {
      // If the mutation fails, use the context we returned above
      if (context?.previousShifts) {
        queryClient.setQueryData(
          queryKeys.shifts.list(),
          context.previousShifts
        );
      }
    },
    onSettled: async () => {
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: queryKeys.shifts.list() }),
        queryClient.invalidateQueries({ queryKey: queryKeys.schedule.all }),
        queryClient.invalidateQueries({
          queryKey: queryKeys.holidays.withShifts(),
        }),
      ]);
    },
  });
};
